<template>
  <div class="gmap-location">
    <div class="gmap-location__box">
      <header class="gmap-location__box__header">
        <ui-button
          class="gmap-location__box__header__close"
          @click.prevent="setCurrentLocation(null)"
          button="secondary"
          :label="$t('commons.buttons.close')"
          :icon-only="true"
          icon="close"
        />
        <div class="gmap-location__box__header__marker">
          <img
            :src="require('@/assets/img/svg/crown.svg')"
            class="gmap-location__box__header__marker__koth"
            v-if="location.first"
          />
          <marker-default
            class="gmap-location__box__header__marker__img"
            :filters="filters"
            :current-value="currentValue(location, source)"
            :is-competitor="!location.brand_status"
          />
        </div>
        <div class="gmap-location__box__header__title">
          {{ location.name }}
        </div>
      </header>
      <div class="gmap-location__box__container">
        <div class="gmap-location__box__container__address">
          <div
            class="gmap-location__box__container__address__line"
            v-for="(address, idx) in location.formatted_address"
            :key="`address-${idx}`"
          >
            {{ address }}
          </div>
        </div>
        <div class="gmap-location__box__container__section">
          <div class="gmap-location__box__container__section__title">
            {{ settings.modal_global_title[$i18n.locale] || settings.modal_global_title.default }}
          </div>
          <div class="gmap-location__box__container__section__content" v-if="settings.type === 'rating'">
            <div class="gmap-location__box__container__section__content__rating">
              <div class="gmap-location__box__container__section__content__rating__value">
                {{ location.display_value[$i18n.locale] || location.display_value.default }}
              </div>
              <div class="gmap-location__box__container__section__content__rating__stars">
                <location-stars :value="location.value" />
              </div>
            </div>
            <div class="gmap-location__box__container__section__content__review">
              {{
                settings.modal_global_label[$i18n.locale]
                  ? settings.modal_global_label[$i18n.locale].replace('%count%', location.count)
                  : settings.modal_global_label.default.replace('%count%', location.count)
              }}
            </div>
          </div>
          <div class="gmap-location__box__container__section__content" v-else>
            <div class="gmap-location__box__container__section__content__value">
              <span :style="{ color: location.display_value_color }">
                {{ location.display_value[$i18n.locale] || location.display_value.default }}
              </span>
            </div>
            <div class="gmap-location__box__container__section__content__label">
              {{
                settings.modal_global_label[$i18n.locale]
                  ? settings.modal_global_label[$i18n.locale].replace('%count%', '')
                  : settings.modal_global_label.default.replace('%count%', '')
              }}
            </div>
          </div>
        </div>
        <div class="gmap-location__box__container__section" v-if="location.details.length > 0">
          <div class="gmap-location__box__container__section__title">
            {{ settings.modal_details_title[$i18n.locale] || settings.modal_details_title.default }}
          </div>
          <template v-if="settings.type === 'rating'">
            <div
              class="gmap-location__box__container__section__content"
              v-for="(detail, idx) in location.details"
              :key="`detail-${idx}`"
            >
              <div class="gmap-location__box__container__section__content__sub-rating">
                <img
                  :src="settings.details[detail.source]"
                  class="gmap-location__box__container__section__content__sub-rating__logo"
                  :alt="detail.source"
                />
                <div class="gmap-location__box__container__section__content__sub-rating__value">
                  {{ detail.display_value[$i18n.locale] || detail.display_value.default }}
                </div>
                <div class="gmap-location__box__container__section__content__sub-rating__stars">
                  <location-stars :value="detail.value" />
                </div>
              </div>
              <div class="gmap-location__box__container__section__content__review">
                {{
                  settings.modal_global_label[$i18n.locale]
                    ? settings.modal_details_label[$i18n.locale].replace('%count%', detail.count)
                    : settings.modal_details_label.default.replace('%count%', detail.count)
                }}
              </div>
            </div>
          </template>
          <template v-else>
            <div
              class="gmap-location__box__container__section__content"
              v-for="(detail, idx) in location.details"
              :key="`detail-${idx}`"
            >
              <div class="gmap-location__box__container__section__content__detail">
                <div class="gmap-location__box__container__section__content__detail__icon backoffice-icons">
                  arrow_right
                </div>
                <div class="gmap-location__box__container__section__content__detail__label">
                  {{
                    settings.detail_labels[detail.source][$i18n.locale] || settings.detail_labels[detail.source].default
                  }}
                </div>
                <div class="gmap-location__box__container__section__content__detail__value">
                  <span :style="{ color: detail.display_value_color }">
                    {{ detail.display_value[$i18n.locale] || detail.display_value.default }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </div>
        <div
          class="gmap-location__box__container__cta"
          v-if="
            location.brand_status &&
              settings.with_local_competition &&
              ($route.name === 'markers' || $route.name === 'division')
          "
        >
          <router-link
            tag="div"
            :to="{
              name: 'location',
              params: { id: $route.params.id, location: location.internal_id },
              query: $route.query,
            }"
            v-slot="{ navigate }"
          >
            <ui-button
              class="gmap-location__box__container__cta__button"
              button="cta"
              variant="gtr"
              :label="settings.modal_button[$i18n.locale] || settings.modal_button.default"
              @click.native="onClickSetCurrentLocation"
              @click="navigate"
            />
          </router-link>
        </div>
        <div class="gmap-location__box__container__cta" v-if="locationHasButtonUrl">
          <ui-button
            class="gmap-location__box__container__cta__button"
            button="cta"
            variant="gtr"
            :label="settings.modal_button_bis[$i18n.locale] || settings.modal_button_bis.default"
            :is-link="true"
            :href="location.button_url"
          />
        </div>
        <location-distance
          class="gmap-location__box__container__section"
          :current-distance="distance > 0 ? distance : location.default_distance"
          :distance-array="location.distances"
          :distance-unit="settings.distance_unit"
          v-if="location.brand_status && $route.name === 'location'"
          @update="updateDistance"
          @refresh="$emit('refreshLocations')"
        />
      </div>
      <footer class="gmap-location__box__footer">
        <ui-button
          class="gmap-location__box__footer__close"
          :label="$t('commons.buttons.close')"
          button="primary"
          @click.prevent="setCurrentLocation(null)"
        />
      </footer>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import MarkerDefault from '@/components/Marker/Default.vue'
import UiButton from '@/components/UI/Button.vue'
import { getValueBySource } from '@/utils/extractor.util'
import LocationStars from '@/components/Location/Stars.vue'
import LocationDistance from '@/components/Location/Distance.vue'
import { launchGAEvent } from '@/utils/gtm.util'

export default {
  name: 'GmapLocation',
  components: {
    MarkerDefault,
    UiButton,
    LocationStars,
    LocationDistance,
  },
  props: {
    location: {
      type: Object,
      required: true,
    },
    source: {
      type: String,
      required: true,
    },
    filters: {
      type: Array,
      required: true,
    },
    settings: {
      type: Object,
      required: true,
    },
    distance: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentValue: getValueBySource,
    }
  },
  computed: {
    locationHasButtonUrl() {
      return this.location?.button_url && location?.button_url !== ''
    },
  },
  methods: {
    ...mapActions({
      setCurrentLocation: 'data/setCurrentLocation',

      updateCurrentDistance: 'refine/updateCurrentDistance',
    }),
    updateDistance(distance) {
      launchGAEvent(this.$route.params.id, 'competition_radius', distance)
      this.updateCurrentDistance(distance)
    },
    onClickSetCurrentLocation() {
      launchGAEvent(this.$route.params.id, 'see_local_competition', this.location.name)
    },
  },
}
</script>

<style lang="scss" scoped>
.gmap-location {
  @include wrapper(flex-start, $gutter-md);

  @media (min-width: $screen-sm) {
    right: initial;
    bottom: initial;
    width: calc(#{$gmap-sidebar-width-tablet} + #{$gutter-md} * 2);
  }

  @media (min-width: $screen-lg) {
    width: calc(#{$gmap-sidebar-width-desktop} + #{$gutter-md} * 2);
  }

  &__box {
    @include box;

    width: $gmap-sidebar-width-mobile;

    @media (min-width: $screen-sm) {
      width: $gmap-sidebar-width-tablet;
    }

    @media (min-width: $screen-lg) {
      width: $gmap-sidebar-width-desktop;
    }

    &__header {
      @include header;

      &__close {
        @include close('top');
      }

      &__marker {
        display: flex;
        position: relative;
        margin-right: 12px;
        width: 20px;

        &__koth {
          position: absolute;
          top: 3px;
          left: 50%;
          transform: translate(-50%, -100%);
          width: 25px;

          @media (max-width: $screen-sm) {
            top: 8px;
          }
        }

        &__img {
          width: 100%;

          @media (max-width: $screen-sm) {
            padding-top: 5px;
          }
        }
      }

      &__title {
        @include title(1rem);

        display: -webkit-box;
        flex: 1;
        padding-bottom: 1px;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }
    }

    &__container {
      @include container;

      &__section {
        margin-top: $spacing-sm;

        @media (min-width: $screen-sm) {
          margin-top: $spacing-md;
        }

        &__title {
          @include sub-title;
        }

        &__content {
          display: flex;
          align-items: center;
          margin-bottom: 10px;

          &:last-child {
            margin-bottom: 0;
          }

          &__value {
            margin-right: 12px;
            line-height: 1;
            font-size: $font-size-icons;
            font-weight: 800;
          }

          &__label {
            font-size: $font-size-sm;
          }

          &__rating,
          &__sub-rating {
            display: flex;
            flex: 1;
            align-items: center;

            &__value {
              margin-right: 12px;
              line-height: 1;
              font-size: $font-size-icons;
              font-weight: 800;
            }

            &__stars {
              width: 120px;
            }
          }

          &__sub-rating {
            &__logo {
              margin-right: 12px;
              width: 32px;
              height: 32px;
            }

            &__value {
              margin-right: 12px;
              width: 25px;
              font-size: $font-size-title;
              font-weight: bold;
            }
          }

          &__review {
            font-size: $font-size-sm;
          }

          &__detail {
            display: flex;
            align-items: center;
            margin-bottom: 5px;

            &:last-child {
              margin-bottom: 0;
            }

            &__icon {
              margin-right: 12px;
            }

            &__label {
              margin-right: 12px;
            }

            &__value {
              font-weight: bold;
            }
          }
        }
      }

      &__cta {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: $spacing-sm;

        @media (min-width: $screen-sm) {
          margin-top: $spacing-md;
        }
      }
    }

    &__footer {
      @include footer;

      @media (min-width: $screen-sm) {
        display: none;
      }
    }
  }
}
</style>
